"use client";

import { useEffect, useState } from "react";
import { AppShell } from "./AppShell";

type AuthUser = {
  id: string;
  username: string;
  role?: "admin" | "user";
  remainingChats?: number;
  membershipPlan?: "free" | "monthly" | "yearly" | "lifetime";
  membershipStatus?: "active" | "expired";
  membershipExpiresAt?: string | null;
  monthlyQuota?: number;
  quotaPeriodEnd?: string | null;
};

type AuthStatus = {
  authenticated: boolean;
  setupRequired: boolean;
  user: AuthUser | null;
};

function getRedemptionCodeFromUrl(): string {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("code") ??
    params.get("redeem") ??
    params.get("redemptionCode") ??
    ""
  ).trim();
}

function AuthForm({ setupRequired, onAuthenticated }: { setupRequired: boolean; onAuthenticated: (user: AuthUser) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [redemptionCode, setRedemptionCode] = useState("");
  const [mode, setMode] = useState<"login" | "register">("login");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isRegister = !setupRequired && mode === "register";

  useEffect(() => {
    if (setupRequired) return;
    const code = getRedemptionCodeFromUrl();
    if (!code) return;
    setRedemptionCode(code);
    setMode("register");
  }, [setupRequired]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(setupRequired ? "/api/app-auth/setup" : isRegister ? "/api/app-auth/register" : "/api/app-auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, redemptionCode: isRegister ? redemptionCode : undefined }),
      });
      const data = await res.json() as { user?: AuthUser; error?: string };
      if (!res.ok || !data.user) {
        throw new Error(data.error ?? "认证失败");
      }
      onAuthenticated(data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="love-auth-page" style={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)", color: "var(--text)", padding: 24 }}>
      <form
        className="love-auth-card"
        onSubmit={submit}
        style={{
          width: "min(360px, 100%)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          padding: 24,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--bg-panel)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 650 }}>{setupRequired ? "创建 Pi Web 账号" : isRegister ? "注册恋爱参谋账号" : "登录恋爱参谋"}</h1>
          <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
            {setupRequired ? "创建此 Pi Web 实例的第一个本地账号。" : isRegister ? "每个 IP 最多注册 2 个账号。" : "使用你的本地账号登录。"}
          </p>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
          用户名
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoFocus
            style={{
              height: 36,
              border: "1px solid var(--border)",
              borderRadius: 6,
              background: "var(--bg)",
              color: "var(--text)",
              padding: "0 10px",
              outline: "none",
            }}
          />
        </label>

        {isRegister && (
          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
            兑换码（选填）
            <input
              value={redemptionCode}
              onChange={(event) => setRedemptionCode(event.target.value)}
              autoComplete="off"
              placeholder="有兑换码可在这里填写"
              style={{
                height: 36,
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: "var(--bg)",
                color: "var(--text)",
                padding: "0 10px",
                outline: "none",
                textTransform: "uppercase",
              }}
            />
          </label>
        )}

        <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
          密码
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            autoComplete={setupRequired || isRegister ? "new-password" : "current-password"}
            style={{
              height: 36,
              border: "1px solid var(--border)",
              borderRadius: 6,
              background: "var(--bg)",
              color: "var(--text)",
              padding: "0 10px",
              outline: "none",
            }}
          />
        </label>

        {error && <div style={{ fontSize: 12, color: "#ef4444", lineHeight: 1.4 }}>{error}</div>}

        <button
          type="submit"
          disabled={busy}
          style={{
            height: 36,
            border: "none",
            borderRadius: 6,
            background: "var(--accent)",
            color: "white",
            fontWeight: 600,
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? "请稍候..." : setupRequired ? "创建账号" : isRegister ? "注册并登录" : "登录"}
        </button>
        {!setupRequired && (
          <button
            type="button"
            onClick={() => {
              setMode((current) => current === "login" ? "register" : "login");
              setError(null);
              setRedemptionCode((current) => current || getRedemptionCodeFromUrl());
            }}
            style={{
              height: 32,
              border: "none",
              background: "none",
              color: "var(--accent)",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {isRegister ? "已有账号，去登录" : "没有账号，立即注册"}
          </button>
        )}
      </form>
    </div>
  );
}

export function AuthGate() {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = async () => {
    const res = await fetch("/api/app-auth/status");
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json() as AuthStatus;
    setStatus(data);
  };

  useEffect(() => {
    let cancelled = false;
    fetch("/api/app-auth/status")
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json() as Promise<AuthStatus>;
      })
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <div style={{ padding: 24, color: "#ef4444" }}>{error}</div>;
  }
  if (!status) {
    return <div className="love-auth-page" style={{ minHeight: "100dvh", display: "grid", placeItems: "center", color: "var(--text-muted)" }}>加载中...</div>;
  }
  if (!status.authenticated || !status.user) {
    return <AuthForm setupRequired={status.setupRequired} onAuthenticated={(user) => setStatus({ authenticated: true, setupRequired: false, user })} />;
  }

  return <AppShell user={status.user} onUserRefresh={() => refreshStatus().catch(() => {})} onLogout={() => setStatus({ authenticated: false, setupRequired: false, user: null })} />;
}
